import React from 'react';
import { Download, Dna, FileText, Leaf } from 'lucide-react';

const Downloads = () => {
    const species = [
        { 
            name: "Tomato",
            scientificName: "Solanum lycopersicum",
            genome: "/downloads/ITAG4_dapfit_scffree.fasta",
            gff: "/downloads/4.0_4.2_merge_on_ITAG4_aegis_fcounts_dapfit.gff3",
            gmt: "/downloads/4.0_4.2_merge_on_ITAG4.gmt"
        },
        { 
            name: "Mulberry",
            scientificName: "Morus alba",
            genome: "/downloads/Mal_HE_154X.fasta",
            gff: "/downloads/Mal_HE_154X_1_on_Mal_HE_154X_aegis_fcounts.gff3",
            gmt: "/downloads/Mal_HE_154X_1_on_Mal_HE_154X.gmt"
        },
        { 
            name: "Grapevine",
            scientificName: "Vitis vinifera",
            genome: "/downloads/T2T_ref_dapfit_scffree.fasta",
            gff: "/downloads/5.1_on_T2T_ref_aegis_fcounts_dapfit.gff3",
            gmt: "/downloads/5.1_on_T2T_ref.gmt"
        },
        { 
            name: "Arabidopsis",
            scientificName: "Arabidopsis thaliana",
            genome: "/downloads/TAIR10_scffree.fasta",
            gff: "/downloads/Araport11_on_TAIR10_aegis_fcounts.gff3",
            gmt: "/downloads/Araport11_on_TAIR10.gmt"
        },
        { 
            name: "Cannabis",
            scientificName: "Cannabis sativa",
            cultivars: [
                {
                    name: "CBDRx",
                    genome: "/downloads/CBDRx.fasta",
                    gff: "/downloads/CBDRx_on_CBDRx_aegis_fcounts.gff3",
                    gmt: "/downloads/CBDRx_on_CBDRx.gmt"
                },
                {
                    name: "Purple Kush",
                    genome: "/downloads/PurpleKush.fasta",
                    gff: "/downloads/PurpleKush_on_PurpleKush_aegis_fcounts.gff3",
                    gmt: "/downloads/PurpleKush_on_PurpleKush.gmt"
                },
                {
                    name: "Jamaican Lion",
                    genome: "/downloads/JamaicanLion.fasta",
                    gff: "/downloads/JamaicanLion_on_JamaicanLion_aegis_fcounts.gff3",
                    gmt: "/downloads/JamaicanLion_on_JamaicanLion.gmt"
                }
            ]
        },
        { 
            name: "Chlamydomonas",
            scientificName: "Chlamydomonas reinhardtii",
            genome: "/downloads/Chlre_6.0.fasta",
            gff: "/downloads/Chlre_6.1_on_Chlre_6.0_aegis_fcounts.gff3",
            gmt: "/downloads/Chlre_6.1_on_Chlre_6.0.gmt"
        },
        { 
            name: "Prunus",
            scientificName: "Prunus persica",
            genome: "/downloads/persica_organellefree.fasta",
            gff: "/downloads/persica_on_persica_aegis_fcounts.gff3",
            gmt: "/downloads/persica_on_persica.gmt"
        },
        { 
            name: "Strawberry",
            scientificName: "Fragaria vesca",
            genome: "/downloads/vesca_organellefree.fasta",
            gff: "/downloads/vesca_on_vesca_aegis_fcounts.gff3",
            gmt: "/downloads/vesca_on_vesca.gmt"
        },
    ];

    return (
        <div className="">
            <div className="max-w-7xl mx-auto">
                {/* Desktop Table View */}
                <div className="hidden md:block bg-white rounded-2xl shadow-xl overflow-hidden">
                    <div className="bg-gradient-to-r from-green-600 to-lime-600 text-white p-6">
                        <h2 className="text-2xl font-semibold flex items-center gap-2">
                            <Dna className="w-6 h-6" />
                            Available Species
                        </h2>
                    </div>
                    
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b-2 border-gray-200">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                        Species
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                        Scientific Name
                                    </th>
                                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                        Genome (FASTA)
                                    </th>
                                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                        Annotation (GFF)
                                    </th>
                                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                        Ontology (GMT)
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {species.map((item, index) => (
                                    <tr 
                                        key={item.name} 
                                        className={`hover:bg-green-50 transition-colors ${
                                            index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                                        }`}
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-3">
                                                <span className="font-semibold text-gray-900">{item.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="text-gray-600 italic">{item.scientificName}</span>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {item.cultivars ? (
                                                <div className="flex flex-col items-center gap-2">
                                                    {item.cultivars.map(cultivar => (
                                                        <a
                                                            key={cultivar.name}
                                                            href={cultivar.genome}
                                                            download
                                                            className="inline-flex w-full max-w-xs justify-center items-center gap-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-medium py-2 px-4 rounded-lg transition-all transform hover:scale-105 shadow-md hover:shadow-lg"
                                                        >
                                                            <Dna className="w-4 h-4" />
                                                            {cultivar.name}
                                                        </a>
                                                    ))}
                                                </div>
                                            ) : (
                                                <a
                                                    href={item.genome}
                                                    download
                                                    className="inline-flex items-center gap-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-medium py-2 px-4 rounded-lg transition-all transform hover:scale-105 shadow-md hover:shadow-lg"
                                                >
                                                    <Dna className="w-4 h-4" />
                                                    Download
                                                </a>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {item.cultivars ? (
                                                <div className="flex flex-col items-center gap-2">
                                                    {item.cultivars.map(cultivar => (
                                                        <a
                                                            key={cultivar.name}
                                                            href={cultivar.gff}
                                                            download
                                                            className="inline-flex w-full max-w-xs justify-center items-center gap-2 bg-gradient-to-r from-lime-600 to-lime-700 hover:from-lime-700 hover:to-lime-800 text-white font-medium py-2 px-4 rounded-lg transition-all transform hover:scale-105 shadow-md hover:shadow-lg"
                                                        >
                                                            <FileText className="w-4 h-4" />
                                                            {cultivar.name}
                                                        </a>
                                                    ))}
                                                </div>
                                            ) : (
                                                <a
                                                    href={item.gff}
                                                    download
                                                    className="inline-flex items-center gap-2 bg-gradient-to-r from-lime-600 to-lime-700 hover:from-lime-700 hover:to-lime-800 text-white font-medium py-2 px-4 rounded-lg transition-all transform hover:scale-105 shadow-md hover:shadow-lg"
                                                >
                                                    <FileText className="w-4 h-4" />
                                                    Download
                                                </a>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {item.cultivars ? (
                                                <div className="flex flex-col items-center gap-2">
                                                    {item.cultivars.map(cultivar => (
                                                        <a
                                                            key={cultivar.name}
                                                            href={cultivar.gmt}
                                                            download
                                                            className="inline-flex w-full max-w-xs justify-center items-center gap-2 bg-gradient-to-r from-lime-600 to-lime-700 hover:from-lime-700 hover:to-lime-800 text-white font-medium py-2 px-4 rounded-lg transition-all transform hover:scale-105 shadow-md hover:shadow-lg"
                                                        >
                                                            <FileText className="w-4 h-4" />
                                                            {cultivar.name}
                                                        </a>
                                                    ))}
                                                </div>
                                            ) : (
                                                <a
                                                    href={item.gmt}
                                                    download
                                                    className="inline-flex items-center gap-2 bg-gradient-to-r from-lime-600 to-lime-700 hover:from-lime-700 hover:to-lime-800 text-white font-medium py-2 px-4 rounded-lg transition-all transform hover:scale-105 shadow-md hover:shadow-lg"
                                                >
                                                    <FileText className="w-4 h-4" />
                                                    Download
                                                </a>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden space-y-4">
                    <div className="bg-gradient-to-r from-green-600 to-lime-600 text-white p-4 rounded-xl">
                        <h2 className="text-xl font-semibold flex items-center gap-2">
                            <Dna className="w-5 h-5" />
                            Available Species
                        </h2>
                    </div>
                    
                    {species.map((item) => (
                        <div 
                            key={item.name} 
                            className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                        >
                            <div className="bg-gradient-to-r from-green-100 to-lime-100 p-4 border-b">
                                <div className="flex items-center gap-3">
                                    <span className="text-3xl">{item.icon}</span>
                                    <div>
                                        <h3 className="font-bold text-lg text-gray-900">{item.name}</h3>
                                        <p className="text-sm text-gray-600 italic">{item.scientificName}</p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="p-4 space-y-3">
                                {item.cultivars ? (
                                    <>
                                        <div className="space-y-2">
                                            <h4 className="font-semibold text-gray-700 text-center text-sm uppercase tracking-wider">Genome (FASTA)</h4>
                                            {item.cultivars.map(cultivar => (
                                                <a key={cultivar.name} href={cultivar.genome} download className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-medium py-3 px-4 rounded-lg transition-all shadow-md hover:shadow-lg">
                                                    <Dna className="w-4 h-4" />
                                                    Download {cultivar.name}
                                                </a>
                                            ))}
                                        </div>
                                        <div className="space-y-2 pt-3 border-t">
                                            <h4 className="font-semibold text-gray-700 text-center text-sm uppercase tracking-wider">Annotation (GFF)</h4>
                                            {item.cultivars.map(cultivar => (
                                                <a key={cultivar.name} href={cultivar.gff} download className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-lime-600 to-lime-700 hover:from-lime-700 hover:to-lime-800 text-white font-medium py-3 px-4 rounded-lg transition-all shadow-md hover:shadow-lg">
                                                    <FileText className="w-4 h-4" />
                                                    Download {cultivar.name}
                                                </a>
                                            ))}
                                        </div>
                                        <div className="space-y-2 pt-3 border-t">
                                            <h4 className="font-semibold text-gray-700 text-center text-sm uppercase tracking-wider">Ontology (GMT)</h4>
                                            {item.cultivars.map(cultivar => (
                                                <a key={cultivar.name} href={cultivar.gmt} download className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-medium py-3 px-4 rounded-lg transition-all shadow-md hover:shadow-lg">
                                                    <Leaf className="w-4 h-4" />
                                                    Download {cultivar.name}
                                                </a>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <a href={item.genome} download className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-medium py-3 px-4 rounded-lg transition-all shadow-md hover:shadow-lg">
                                            <Dna className="w-4 h-4" />
                                            Download Genome (FASTA)
                                        </a>
                                        <a href={item.gff} download className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-lime-600 to-lime-700 hover:from-lime-700 hover:to-lime-800 text-white font-medium py-3 px-4 rounded-lg transition-all shadow-md hover:shadow-lg">
                                            <FileText className="w-4 h-4" />
                                            Download Annotation (GFF)
                                        </a>
                                        <a href={item.gmt} download className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-medium py-3 px-4 rounded-lg transition-all shadow-md hover:shadow-lg">
                                            <Leaf className="w-4 h-4" />
                                            Download Ontology (GMT)
                                        </a>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Footer Information */}
                <div className="mt-12 bg-white/80 backdrop-blur rounded-xl p-6 shadow-lg">
                    <div className="flex items-start gap-3">
                        <Download className="w-5 h-5 text-green-600 mt-1" />
                        <div className="text-sm text-gray-600 space-y-2">
                            <p className="font-semibold text-gray-700">File Format Information:</p>
                            <ul className="space-y-1">
                                <li>• <strong>FASTA files:</strong> Contain nucleotide sequences of complete genomes.</li>
                                <li>• <strong>GFF files:</strong> Include gene annotations and structural information.</li>
                                <li>• <strong>GMT files:</strong> Contain gene sets for ontology terms (Gene Matrix Transposed).</li>
                            </ul>
                            <p className="text-xs mt-3 text-gray-500">
                                Right-click and select "Save Link As" if download doesn't start automatically.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Downloads;
